import { useEffect, useRef, useState } from "react";
import styles from "./EditPatient.module.css";
import EtatCivilField from "./EtatCivilField";
import _ from "underts";
import {
  etatCivilModeAtom,
  FamilleGridOpenState,
  useSelectedPatientIds,
} from "../../main/state/globalState";
import { useAtom } from "jotai";
import {
  CadendarConsts,
  newPatientSchema,
  updatePatientPropsSchema,
} from "cadendar-shared";
import useTempPatient from "../../ficheAppel/hooks/useTempPatient";
import { trpc } from "../../main/components/trpc.ts";
import classNames from "classnames";
import {Button} from "@/components/ui/button";
import { convertLocalNewPatientToNewPatient } from "../../famille/filterTransforms/convertLocalNewPatientToNewPatient.ts";
import { skipToken } from "@tanstack/react-query";
import { getBlankPatient } from "../filtersTransforms/patientTransfomers.ts";
import { RichTextarea } from "rich-textarea";
import {
  getDefaultReminderConsentInput,
  getPatientDTOFromTempInput,
  patientToReminderConsentInput,
  patientToTempInput,
  validate,
  validatorMap,
  converters,
} from "./EditPatient.helpers.ts";
import type { ReminderConsentInput } from "./EditPatient.helpers.ts";
import ReminderConsentToggle from "./ReminderConsentToggle.tsx";

const { etatCivilStrings } = CadendarConsts;

type keys = keyof typeof etatCivilStrings;

const getMissingReminderConsentLabels = (input: ReminderConsentInput) => {
  const missingLabels: string[] = [];

  if (input.email_consent === null) {
    missingLabels.push("mail");
  }
  if (input.sms_consent === null) {
    missingLabels.push("SMS");
  }

  return missingLabels;
};

interface EditPatientProps {
  selectedPatientId: string | null;
}

const EditPatient = (props: EditPatientProps) => {
  const [etatCivilMode, setEtatCivilMode] = useAtom(etatCivilModeAtom);
  const isEditing =
    etatCivilMode === "editPatient" || etatCivilMode === "newPatient";
  const { data: selectedPatient } = trpc.patient.getById.useQuery(
    props.selectedPatientId || skipToken
  );
  const ref = useRef<HTMLDivElement>(null);
  const { tempPatient } = useTempPatient();
  const _hasFamille = !!selectedPatient && !!selectedPatient.famille_id;

  const [tempInput, setTempInput] = useState(patientToTempInput(tempPatient));
  const [reminderConsentInput, setReminderConsentInput] =
    useState<ReminderConsentInput>(getDefaultReminderConsentInput());
  console.log("tempInput", tempInput);
  useEffect(() => {
    setTempInput(patientToTempInput(tempPatient));
  }, [JSON.stringify(tempPatient)]);
  useEffect(() => {
    if (!selectedPatient) return;
    setTempInput(patientToTempInput(selectedPatient));
  }, [JSON.stringify(selectedPatient)]);
  useEffect(() => {
    if (etatCivilMode === "editPatient" && selectedPatient) {
      setReminderConsentInput(patientToReminderConsentInput(selectedPatient));
      return;
    }
    if (etatCivilMode === "newPatient") {
      setReminderConsentInput(getDefaultReminderConsentInput());
    }
  }, [
    etatCivilMode,
    selectedPatient?.id,
    selectedPatient?.email_consent,
    selectedPatient?.sms_consent,
  ]);
  // useLayoutEffect(() => {
  //   if (ref.current) {
  //     ref.current.scrollIntoView();
  //   }
  // }, []);
  const initialTouchedState = Object.keys(etatCivilStrings).reduce(
    (result, key) => ({ ...result, [key]: false }),
    {} as { [K in keys]: boolean }
  );

  const [touchedState, setTouchedState] = useState(initialTouchedState);
  const onCancelPatientEditing = () => {
    setEtatCivilMode("viewPatient");
  };
  const [_ignored, { selectPatientId }] = useSelectedPatientIds();
  const utils = trpc.useUtils();
  const commitNewPatientMutation = trpc.patient.create.useMutation({
    onSuccess: (value: string) => {
      setEtatCivilMode("viewPatient");
      utils.patient.invalidate();
      utils.rdv.invalidate(); //on invalide aussi les rdvs car si le nom du patient est modifié ça modifie le rdv
      selectPatientId(value);
      setEtatCivilMode("viewPatient");
    },
  });
  const commitEditPatientMutation = trpc.patient.update.useMutation({
    onSuccess: () => {
      setEtatCivilMode("viewPatient");
      utils.patient.invalidate();
      utils.rdv.invalidate();
    },
  });
  const onSubmit = () => {
    console.log("etatCivilMode", etatCivilMode);
    if (etatCivilMode === "newPatient") {
      console.log("tempPatient", tempPatient);
      const newPatientDTO = {
        ...getBlankPatient(),
        ...(tempPatient.name
          ? convertLocalNewPatientToNewPatient(tempPatient)
          : null),
        ...getPatientDTOFromTempInput(tempInput, reminderConsentInput),
      };
      console.log("newPatientDTO", newPatientDTO);
      commitNewPatientMutation.mutate(newPatientSchema.parse(newPatientDTO));

      return;
    }
    if (etatCivilMode === "editPatient") {
      if (!selectedPatient)
        throw new Error("selectedPatient is null in onSubmit");
      const patientDTO = {
        ...selectedPatient,
        ...getPatientDTOFromTempInput(tempInput, reminderConsentInput),
      };

      commitEditPatientMutation.mutate({
        id: patientDTO.id,
        props: updatePatientPropsSchema.parse({ ...patientDTO }),
      });
      setEtatCivilMode("viewPatient");
      return;
    }
  };
  const onClickPatientEdit = () => setEtatCivilMode("editPatient");
  const [, openFamilleGrid] = useAtom(FamilleGridOpenState);
  const onClickOpenFamilleGrid = () => openFamilleGrid(true);
  const errors = validate(tempInput);

  const newPatientFields = Object.keys(etatCivilStrings) as keys[];

  const handleTempInput = (key: keys, value: string) => {
    setTempInput((tempInput) => ({ ...tempInput, [key]: value }));
    setTouchedState((state) => ({ ...state, [key]: true }));
  };

  const handleBlur = (key: keys) => {
    const value = tempInput[key];
    if (validatorMap[key](value)) {
      const converted = converters[key](value) || "";
      setTempInput((state) => ({ ...state, [key]: converted }));
    }
  };

  const handleGenreClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("handleGenreClick", e);
    e.stopPropagation();
    const value = e.currentTarget.value as "M" | "F";
    setTempInput((state) => ({ ...state, GENRE: value }));
    setTouchedState((state) => ({ ...state, GENRE: true }));
  };
  const handleReminderConsentChange = (
    key: keyof ReminderConsentInput,
    value: boolean | null,
  ) => {
    setReminderConsentInput((state) => ({
      ...state,
      [key]: value,
    }));
  };

  const handleAllowAllReminderConsents = () => {
    setReminderConsentInput({
      email_consent: true,
      sms_consent: true,
    });
  };
  const displayedReminderConsentInput = isEditing
    ? reminderConsentInput
    : patientToReminderConsentInput(selectedPatient);
  const missingReminderConsentLabels = getMissingReminderConsentLabels(
    displayedReminderConsentInput,
  );
  return (
    <div
      className={classNames(
        {
          [styles.isEditing]: isEditing,
        },
        styles.container
      )}
      ref={ref}
    >
      <div>
        {isEditing ? (
          ""
        ) : (
          <div className={styles.spacer}>
            <Button onClick={onClickPatientEdit} id="etat_civil_edit" size="sm" variant="outline">
              Editer
            </Button>
          </div>
        )}
        <div className="pt-3 -mb-2 flex flex-row gap-2">
          <input
            type="radio"
            name="genre"
            value="M"
            checked={tempInput["GENRE"] === "M"}
            onChange={handleGenreClick}
            disabled={!isEditing}
          />
          <label htmlFor="M">M</label>
          <input
            type="radio"
            name="genre"
            value="F"
            checked={tempInput["GENRE"] === "F"}
            onChange={handleGenreClick}
            disabled={!isEditing}
          />
          <label htmlFor="F">F</label>
        </div>
        {newPatientFields.map((key) => {
          if (key === "COMMENTS") {
            const isInvalid = touchedState[key] && !!errors[key];
            const style: React.CSSProperties = {
              width: "100%",
              minHeight: "100px",
              border: "1px solid",
              borderColor: isInvalid ? "red" : "lightgrey",
              borderRadius: "5px",
              padding: "10px",
              transition:
                "border-color .15s ease-in-out, box-shadow .15s ease-in-out",
              backgroundColor: "white",
              outline: "none",
              // boxShadow: isCommentsFocused
              //   ? `rgba(13, 110, 253, 0.58) 0 0 0 3px`
              //   : "none",
            };

            return (
              <div
                className="flex flex-col items-start mt-4 mb-4 gap-1"
                key={"EtatCivilFieldKey" + key}
              >
                <label htmlFor={key} className="">
                  {etatCivilStrings[key]}
                </label>
                <RichTextarea
                  value={tempInput[key] || ""}
                  onChange={(e) => handleTempInput(key, e.target.value)}
                  onBlur={() => {
                    handleBlur(key);
                  }}
                  readOnly={!isEditing}
                  style={style}
                />
              </div>
            );
          } else {
            return (
              <EtatCivilField
                errors={errors[key]}
                label={etatCivilStrings[key]}
                id={key}
                name={key}
                value={tempInput[key] || ""}
                onChange={(value) => handleTempInput(key, value)}
                onBlur={() => {
                  handleBlur(key);
                }}
                touched={touchedState[key]}
                isEditing={isEditing}
                key={"EtatCivilFieldKey" + key}
                isValid={validatorMap[key](tempInput[key])}
              />
            );
          }
        })}

        {missingReminderConsentLabels.length > 0 ? (
          <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-amber-900">
            Consentements rappels a renseigner : {missingReminderConsentLabels.join(", ")}
          </div>
        ) : null}

        {isEditing ? (
          <div className={styles.consentSection}>
            <div className={styles.consentHeader}>
              <span>Autorisations rappels</span>
              <Button
                onClick={handleAllowAllReminderConsents}
                variant="secondary"
                size="sm"
                id="button_allow_all_reminders"
              >
                Tout autoriser
              </Button>
            </div>
            <ReminderConsentToggle
              value={reminderConsentInput.email_consent}
              onChange={(value) =>
                handleReminderConsentChange("email_consent", value)
              }
              label="Autorisation mail"
              id="patient_email_consent"
            />
            <ReminderConsentToggle
              value={reminderConsentInput.sms_consent}
              onChange={(value) =>
                handleReminderConsentChange("sms_consent", value)
              }
              label="Autorisation SMS"
              id="patient_sms_consent"
            />
          </div>
        ) : null}

        {isEditing ? (
          <div className={styles.buttonsSpacer}>
            <Button
              onClick={onSubmit}
              id="button_save_patient"
              disabled={Object.keys(errors).length > 0}
            >
              Enregistrer
            </Button>
            <Button
              onClick={onCancelPatientEditing}
              variant="secondary"
              id="button_cancel_patient"
            >
              Annuler
            </Button>
          </div>
        ) : (
          ""
        )}

        {isEditing ? (
          ""
        ) : _hasFamille ? (
          <Button
            onClick={onClickOpenFamilleGrid}
            id={"open_famille_dialog"}
            variant="default"
            size="sm"
          >
            Editer Famille Existante
          </Button>
        ) : (
          <Button
            onClick={onClickOpenFamilleGrid}
            id={"open_famille_dialog"}
            size="sm"
            variant="outline"
          >
            Créer Famille
          </Button>
        )}
      </div>
    </div>
  );
};

export default EditPatient;
